import { useSyncExternalStore } from "react";

/**
 * The period picked on Overview, Accountant and the client's Accountant tab is one shared choice:
 * set it on one page and the others open on it. It lives for the browser tab's session (sessionStorage),
 * so it survives navigation and reloads but not closing the tab.
 */
export interface SharedPeriod {
	/** Quick period index used by every PeriodSelector: today, yesterday, last 7 / 30 days, this month, last month. */
	selectedDate: number;
	/** A custom range overrides the quick period until another quick period is picked. */
	manualRange: { from: string; to: string } | null;
}

const STORAGE_KEY = "crm.sharedPeriod";
const QUICK_PERIODS = 6;
const DEFAULT_PERIOD: SharedPeriod = { selectedDate: 3, manualRange: null };
const isDay = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const read = (): SharedPeriod => {
	try {
		const raw = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
		if (raw && Number.isInteger(raw.selectedDate) && raw.selectedDate >= 0 && raw.selectedDate < QUICK_PERIODS) {
			const range = raw.manualRange;
			return { selectedDate: raw.selectedDate, manualRange: range && isDay(range.from) && isDay(range.to) && range.from <= range.to ? { from: range.from, to: range.to } : null };
		}
	} catch {
		// Storage blocked or the value corrupted: start from the default period.
	}
	return DEFAULT_PERIOD;
};

let current = read();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => { listeners.delete(listener); };
};
const getSnapshot = () => current;

const update = (next: SharedPeriod) => {
	current = next;
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
	} catch {
		// Without storage the choice is still shared in memory until a reload.
	}
	listeners.forEach(listener => listener());
};

export const selectSharedPreset = (index: number) => update({ selectedDate: index, manualRange: null });
export const selectSharedRange = (from: string, to: string) => update({ selectedDate: current.selectedDate, manualRange: { from, to } });

export const useSharedPeriod = () => useSyncExternalStore(subscribe, getSnapshot);
